import type {
  OrderCreateRetailerMetadataType,
  OrderV2Dto,
} from "@sellar-io/api-v2/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../../useSellarCurrentBusiness.shared";
import {
  apiV2AssertResponse,
  apiV2Request,
  generateSellarMetaData,
} from "../../apiV2Request";
import { ServerStateKeys } from "../../ServerStateKeys";

export const useOrderCreate = ({
  invalidateUnifiedCheckoutBasket = true,
}: {
  invalidateUnifiedCheckoutBasket?: boolean;
} = {}) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: {
      data: OrderV2Dto.OrderCreateRequestType;
      idempotencyKey?: string;
      sellarMetadata?: OrderCreateRetailerMetadataType;
    }) =>
      apiV2Request({
        user,
        business,
        method: "post",
        path: `orders/v2`,
        data: request.data,
        idempotencyKey: request.idempotencyKey,
        sellarMetadata: request.sellarMetadata
          ? generateSellarMetaData(request.sellarMetadata)
          : undefined,
      }).then(
        (response) =>
          apiV2AssertResponse(response)
            .data as OrderV2Dto.OrderCreateResponseType,
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [user?.uid, business?.id, ServerStateKeys.SCOrders],
        }),
        queryClient.invalidateQueries({
          queryKey: [user?.uid, business?.id, ServerStateKeys.LegalEntity],
        }),
        ...(invalidateUnifiedCheckoutBasket
          ? [
              queryClient.invalidateQueries({
                queryKey: [
                  user?.uid,
                  business?.id,
                  ServerStateKeys.UnifiedCheckoutBaskets,
                ],
              }),
            ]
          : []),
        queryClient.invalidateQueries({
          queryKey: [
            user?.uid,
            business?.id,
            ServerStateKeys.LegalEntityCreditLimitInfo,
          ],
        }),
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            queryKey[0] === user?.uid &&
            queryKey[1] === business?.id &&
            queryKey.at(-1) === ServerStateKeys.Checkout,
        }),
      ]);
    },
  });
};
